import { formatCents } from '@/libs/utils/format-money';
import {
  DASHBOARD_TODO_SOURCE_KEYS,
  GiftBlockedReasonCode,
  type DashboardTodo,
  type DashboardTodoSourceKey,
} from '@fluenceur/types';
import type { TFunction } from 'i18next';

/**
 * What the to-do card shows, decided from the server's answer without any
 * rendering: which lines appear, the total in the header, and each subtitle.
 *
 * A line appears when its source was read and counts something, or when it
 * failed (so it can be retried). A source the brand cannot open (`unavailable`)
 * and a line at zero never appear.
 */

export type TodoLine =
  | { key: DashboardTodoSourceKey; status: 'ok'; count: number }
  | { key: DashboardTodoSourceKey; status: 'failed' };

/** Where each line leads: the page whose badge or counter it repeats. */
export const TODO_LINE_HREF: Record<DashboardTodoSourceKey, string> = {
  applications: '/applications',
  ugcDecisions: '/collabs/ugc',
  blockedGifts: '/gifting?giftFilter=to_handle',
  owedCommissions: '/payments',
};

/** Each line's label, written out so the locale checker sees every key. */
export const TODO_LINE_LABEL_KEY: Record<DashboardTodoSourceKey, string> = {
  applications: 'dashboard.todo.lines.applications.label',
  ugcDecisions: 'dashboard.todo.lines.ugcDecisions.label',
  blockedGifts: 'dashboard.todo.lines.blockedGifts.label',
  owedCommissions: 'dashboard.todo.lines.owedCommissions.label',
};

/** The most frequent blocking cause, as the blocked gifts line words it (pluralised on the count). */
const GIFT_REASON_KEY: Record<GiftBlockedReasonCode, string> = {
  [GiftBlockedReasonCode.VARIANT_OUT_OF_STOCK]:
    'dashboard.todo.lines.blockedGifts.reason.VARIANT_OUT_OF_STOCK',
  [GiftBlockedReasonCode.NO_SHIPPING_RATE]:
    'dashboard.todo.lines.blockedGifts.reason.NO_SHIPPING_RATE',
  [GiftBlockedReasonCode.ADDRESS_INVALID]:
    'dashboard.todo.lines.blockedGifts.reason.ADDRESS_INVALID',
  [GiftBlockedReasonCode.SHOPIFY_PERMISSION]:
    'dashboard.todo.lines.blockedGifts.reason.SHOPIFY_PERMISSION',
  [GiftBlockedReasonCode.SHOPIFY_RATE_LIMITED]:
    'dashboard.todo.lines.blockedGifts.reason.SHOPIFY_RATE_LIMITED',
  [GiftBlockedReasonCode.RECONCILIATION_REQUIRED]:
    'dashboard.todo.lines.blockedGifts.reason.RECONCILIATION_REQUIRED',
  [GiftBlockedReasonCode.UNKNOWN]: 'dashboard.todo.lines.blockedGifts.reason.UNKNOWN',
};

export const visibleTodoLines = (todo: DashboardTodo): TodoLine[] =>
  DASHBOARD_TODO_SOURCE_KEYS.flatMap((key): TodoLine[] => {
    const result = todo[key];
    if (result.status === 'failed') return [{ key, status: 'failed' }];
    if (result.status === 'ok' && result.data.count > 0) {
      return [{ key, status: 'ok', count: result.data.count }];
    }
    return [];
  });

/** The header pill: everything the card counts, over the lines it could read. */
export const todoTotal = (todo: DashboardTodo): number =>
  visibleTodoLines(todo).reduce(
    (total, line) => total + (line.status === 'ok' ? line.count : 0),
    0
  );

/** The sentence under a line's label, in the reader's language. Empty when there is nothing to say. */
export function todoLineSubtitle<K extends DashboardTodoSourceKey>(
  result: DashboardTodo[K],
  key: K,
  t: TFunction,
  locale: string
): string {
  if (result.status !== 'ok') return '';

  switch (key) {
    case 'applications': {
      const data = (result as Extract<DashboardTodo['applications'], { status: 'ok' }>).data;
      return data.includesBriefs
        ? t('dashboard.todo.lines.applications.subtitleWithBriefs')
        : t('dashboard.todo.lines.applications.subtitle');
    }
    case 'ugcDecisions': {
      const data = (result as Extract<DashboardTodo['ugcDecisions'], { status: 'ok' }>).data;
      return [
        data.choices > 0
          ? t('dashboard.todo.lines.ugcDecisions.choices', { count: data.choices })
          : null,
        data.reviewsToday > 0
          ? t('dashboard.todo.lines.ugcDecisions.reviewsToday', { count: data.reviewsToday })
          : null,
      ]
        .filter(Boolean)
        .join(' · ');
    }
    case 'blockedGifts': {
      const data = (result as Extract<DashboardTodo['blockedGifts'], { status: 'ok' }>).data;
      return data.topReasonCode
        ? t(GIFT_REASON_KEY[data.topReasonCode], { count: data.count })
        : '';
    }
    case 'owedCommissions': {
      const data = (result as Extract<DashboardTodo['owedCommissions'], { status: 'ok' }>).data;
      if (!data.headline) return '';
      const amount = formatCents(
        data.headline.totalAmountCents,
        data.headline.currencyCode,
        locale,
        { maximumFractionDigits: 0 }
      );
      return data.hasOtherCurrencies
        ? t('dashboard.todo.lines.owedCommissions.subtitleWithOtherCurrencies', { amount })
        : t('dashboard.todo.lines.owedCommissions.subtitle', { amount });
    }
    default:
      return '';
  }
}
